import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Select, message, Tooltip } from 'antd';
import {
	DownloadOutlined,
	EyeOutlined,
	UploadOutlined,
	FileTextOutlined,
	FilePdfOutlined,
	CloseOutlined,
	ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useFormikContext, Field } from 'formik';
import styled from 'styled-components';
import { Box, Text } from 'atoms';
import {
	SkipDocumentUploadRow,
	SkipDocumentCheckbox,
	SkipDocumentInfoIcon,
} from 'pagesv2/vehiclesv2/create-vehiclev2/CreateVehiclePageV2.styles';
import FormField from './FormField';
import FilePreviewModal from '../../common/FilePreviewModal';
import BulkUploadModal from '../../modals/BulkUploadModal';
import {
	VEHICLE_NUMBER_PATTERN,
	VEHICLE_NUMBER_MESSAGE,
} from 'pagesv2/vehiclesv2/create-vehiclev2/CreateVehiclePageV2.constants';
import {
	RC_DOCUMENT_LABEL,
	INSURANCE_DOCUMENT_LABEL,
	UPLOADING_TEXT,
	BROWSE_FILE_TEXT,
} from './VehicleInformationStep.constants';
import {
	HeaderContainer,
	SectionTitle,
	BulkUploadButton,
	StyledInput,
	StyledSelect,
	StyledDatePicker,
	BrowseButton,
	AddVehicleButton,
	StyledTable,
	ViewButton,
	DocumentPreviewIcon,
	PendingDocumentStatus,
	FilePreview,
	FileIcon,
	FileName,
	FileSize,
	RemoveFileButton,
	RemoveButton,
	SaveButton,
	ConsentLetterBrowseButton,
} from './VehicleInformationStep.styled';
import { getTableColumns } from './VehicleInformationStep.columns';
import { VehicleDetailsModal } from './VehicleInformationStep.modal';
import useVehicleInformationStepLogic from './useVehicleInformationStepLogic';

const { Option } = Select;

// Styled components for file display
const FileItem = styled(Box)`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 10px 16px;
	background-color: #f0f5ff;
	border: 1px solid #d9d9d9;
	border-radius: 4px;
	margin-top: 12px;
	gap: 8px;
`;

const FileLink = styled(Text)`
	color: #1890ff;
	cursor: pointer;
	text-decoration: none;

	&:hover {
		text-decoration: underline;
	}
`;

// Helper function to extract filename from S3 key
const extractFilenameFromS3Key = (s3Key) => {
	if (!s3Key) return '';
	const uuidPattern = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}-/i;
	const match = s3Key.match(uuidPattern);
	if (match) {
		return s3Key.substring(match[0].length);
	}
	return s3Key;
};

// Helper component to display field errors
function FieldError({ error, touched }) {
	if (!error || !touched) return null;
	return <div style={{ color: '#ff4d4f', fontSize: '14px', marginTop: '4px' }}>{error}</div>;
}

FieldError.propTypes = {
	error: PropTypes.string,
	touched: PropTypes.bool,
};

// eslint-disable-next-line react/no-multi-comp
export default function VehicleInformationStep({ isEditable = true, isDisabled = false }) {
	const { values, setFieldValue } = useFormikContext();
	const disabled = !isEditable || isDisabled;

	// Use custom hook for all state and handlers
	const {
		modalVisible,
		selectedVehicle,
		editingVehicle,
		filePreviewVisible,
		previewFileKey,
		previewFileName,
		savingVehicle,
		vehicleTypeOptions,
		loadingVehicleTypes,
		vehicles,
		uploading,
		rcDocFile,
		setRcDocFile,
		insuranceFile,
		setInsuranceFile,
		pucFile,
		setPucFile,
		consentLetterFile,
		setConsentLetterFile,
		bulkUploadModalVisible,
		bulkUploadLoading,
		rcDocFileInputRef,
		insuranceFileInputRef,
		pucDocFileInputRef,
		consentLetterFileInputRef,
		handleFileChangeEvent,
		handleFileUploadClick,
		handleFileRemove,
		handleBulkUpload,
		handleBulkUploadFileUpload,
		handleBulkUploadSubmit,
		handleBulkUploadModalClose,
		handleAddVehicle,
		handleViewVehicle,
		handleModalClose,
		handleSaveChanges,
		handleRemoveVehicle,
		handleDocumentPreview,
		handleFilePreviewClose,
		setEditingVehicle,
	} = useVehicleInformationStepLogic(disabled);

	// Get table columns with handlers
	const columns = getTableColumns({
		handleDocumentPreview,
		handleViewVehicle,
		RC_DOCUMENT_LABEL,
		INSURANCE_DOCUMENT_LABEL,
		DocumentPreviewIcon,
		PendingDocumentStatus,
		ViewButton,
		EyeOutlined,
		FileTextOutlined,
		ExclamationCircleOutlined,
	});

	return (
		<>
			{/* Hidden file inputs */}
			<input
				type="file"
				ref={rcDocFileInputRef}
				style={{ display: 'none' }}
				accept=".pdf,.jpg,.jpeg,.png"
				disabled={disabled}
				onChange={(e) => !disabled && handleFileChangeEvent(e, 'rc_doc')}
			/>
			<input
				type="file"
				ref={insuranceFileInputRef}
				style={{ display: 'none' }}
				accept=".pdf,.jpg,.jpeg,.png"
				disabled={disabled}
				onChange={(e) => !disabled && handleFileChangeEvent(e, 'insurance')}
			/>
			<input
				type="file"
				ref={pucDocFileInputRef}
				style={{ display: 'none' }}
				accept=".pdf,.jpg,.jpeg,.png"
				disabled={disabled}
				onChange={(e) => !disabled && handleFileChangeEvent(e, 'puc_doc')}
			/>
			<input
				type="file"
				ref={consentLetterFileInputRef}
				style={{ display: 'none' }}
				accept=".pdf,.jpg,.jpeg,.png"
				disabled={disabled}
				onChange={(e) => !disabled && handleFileChangeEvent(e, 'consent_letter')}
			/>

			<HeaderContainer>
				<SectionTitle>Onboard Vehicle</SectionTitle>
				<BulkUploadButton
					icon={<UploadOutlined />}
					disabled={disabled}
					onClick={() => !disabled && handleBulkUpload()}
				>
					Bulk Upload
				</BulkUploadButton>
			</HeaderContainer>

			{/* Vehicle Type & Vehicle Number */}
			<Row gutter={[24, 0]}>
				<Col span={12}>
					<FormField label="Vehicle Type" required>
						<Field name="vehicle_type_id">
							{({ field, form }) => (
								<>
									<StyledSelect
										{...field}
										showSearch
										placeholder={
											loadingVehicleTypes
												? 'Loading vehicle types...'
												: 'Search and select'
										}
										disabled={disabled || loadingVehicleTypes}
										loading={loadingVehicleTypes}
										value={form.values.vehicle_type_id}
										onChange={(value) => {
											form.setFieldValue('vehicle_type_id', value);
											// Clear error when user selects a value
											if (form.values.vehicleErrors?.vehicle_type_id) {
												const newErrors = { ...form.values.vehicleErrors };
												delete newErrors.vehicle_type_id;
												form.setFieldValue('vehicleErrors', newErrors);
												form.setFieldValue(
													'vehicle_type_id_touched',
													false
												);
											}
										}}
										filterOption={(input, option) =>
											(option?.children ?? '')
												.toLowerCase()
												.includes(input.toLowerCase())
										}
										notFoundContent={
											loadingVehicleTypes
												? 'Loading...'
												: 'No vehicle types found'
										}
										status={
											form.values.vehicleErrors?.vehicle_type_id &&
											form.values.vehicle_type_id_touched
												? 'error'
												: ''
										}
									>
										{vehicleTypeOptions.map((option) => (
											<Option key={option.id} value={option.id}>
												{option.label}
											</Option>
										))}
									</StyledSelect>
									<FieldError
										error={form.values.vehicleErrors?.vehicle_type_id}
										touched={form.values.vehicle_type_id_touched}
									/>
								</>
							)}
						</Field>
					</FormField>
				</Col>
				<Col span={12}>
					<FormField label="Vehicle Number" required>
						<Field name="vehicle_number">
							{({ field, form }) => (
								<>
									<StyledInput
										{...field}
										placeholder="Eg : DL01AB1234, 22BH1234AB"
										disabled={disabled}
										onChange={(e) => {
											form.setFieldValue('vehicle_number', e.target.value);
											// Clear error when user types
											if (form.values.vehicleErrors?.vehicle_number) {
												const newErrors = { ...form.values.vehicleErrors };
												delete newErrors.vehicle_number;
												form.setFieldValue('vehicleErrors', newErrors);
												form.setFieldValue('vehicle_number_touched', false);
											}
										}}
										onBlur={(e) => {
											const { value } = e.target;
											if (value && !VEHICLE_NUMBER_PATTERN.test(value)) {
												form.setFieldValue('vehicleErrors', {
													...(form.values.vehicleErrors || {}),
													vehicle_number: VEHICLE_NUMBER_MESSAGE,
												});
												form.setFieldValue('vehicle_number_touched', true);
											}
										}}
										status={
											form.values.vehicleErrors?.vehicle_number &&
											form.values.vehicle_number_touched
												? 'error'
												: ''
										}
									/>
									<FieldError
										error={form.values.vehicleErrors?.vehicle_number}
										touched={form.values.vehicle_number_touched}
									/>
								</>
							)}
						</Field>
					</FormField>
				</Col>
			</Row>

			{/* Vehicle RC Doc Upload & Valid Till */}
			<Row gutter={[24, 0]}>
				<Col span={6}>
					<FormField
						label="Vehicle RC Doc Upload"
						required={!values.skip_document_upload}
					>
						<Field name="rc_doc_file">
							{({ form }) => (
								<>
									<BrowseButton
										icon={<UploadOutlined />}
										loading={uploading.rc_doc}
										disabled={disabled || values.skip_document_upload}
										onClick={() =>
											!disabled &&
											!uploading.rc_doc &&
											handleFileUploadClick('rc_doc')
										}
										style={{ width: '100%' }}
									>
										{uploading.rc_doc ? UPLOADING_TEXT : BROWSE_FILE_TEXT}
									</BrowseButton>
									{rcDocFile && (
										<FileItem>
											<Box display="flex" alignItems="center" gap={8}>
												<FilePdfOutlined
													style={{ color: '#ff4d4f', fontSize: '18px' }}
												/>
												<FileLink fontSize={14}>
													{extractFilenameFromS3Key(rcDocFile.name)}
												</FileLink>
											</Box>
											<Box display="flex" alignItems="center" gap={8}>
												<EyeOutlined
													style={{
														cursor: 'pointer',
														color: '#1890ff',
														fontSize: '16px',
													}}
													onClick={() =>
														handleDocumentPreview(
															values.rc_document_key,
															rcDocFile.name
														)
													}
												/>
												{!disabled && (
													<CloseOutlined
														style={{
															cursor: 'pointer',
															color: '#ff4d4f',
															fontSize: '14px',
														}}
														onClick={() => handleFileRemove('rc_doc')}
													/>
												)}
											</Box>
										</FileItem>
									)}
									<FieldError
										error={form.values.vehicleErrors?.rc_document_key}
										touched={form.values.rc_document_key_touched}
									/>
								</>
							)}
						</Field>
					</FormField>
				</Col>
				<Col span={6}>
					<FormField label="Valid Till" required={!values.skip_document_upload}>
						<Field name="rc_valid_till">
							{({ field, form }) => (
								<>
									<StyledDatePicker
										{...field}
										value={form.values.rc_valid_till}
										placeholder="Eg : 12-2-2025"
										format="DD-MM-YYYY"
										disabled={disabled || values.skip_document_upload}
										onChange={(date) => {
											form.setFieldValue('rc_valid_till', date);
											// Clear error when user selects a date
											if (form.values.vehicleErrors?.rc_valid_till) {
												const newErrors = { ...form.values.vehicleErrors };
												delete newErrors.rc_valid_till;
												form.setFieldValue('vehicleErrors', newErrors);
												form.setFieldValue('rc_valid_till_touched', false);
											}
										}}
										status={
											form.values.vehicleErrors?.rc_valid_till &&
											form.values.rc_valid_till_touched
												? 'error'
												: ''
										}
									/>
									<FieldError
										error={form.values.vehicleErrors?.rc_valid_till}
										touched={form.values.rc_valid_till_touched}
									/>
								</>
							)}
						</Field>
					</FormField>
				</Col>
				<Col span={6}>
					<FormField label="Insurance Doc Upload" required={!values.skip_document_upload}>
						<Field name="insurance_file">
							{({ form }) => (
								<>
									<BrowseButton
										icon={<UploadOutlined />}
										loading={uploading.insurance}
										disabled={disabled || values.skip_document_upload}
										onClick={() =>
											!disabled &&
											!uploading.insurance &&
											handleFileUploadClick('insurance')
										}
										style={{ width: '100%' }}
									>
										{uploading.insurance ? UPLOADING_TEXT : BROWSE_FILE_TEXT}
									</BrowseButton>
									{insuranceFile && (
										<FileItem>
											<Box display="flex" alignItems="center" gap={8}>
												<FilePdfOutlined
													style={{ color: '#ff4d4f', fontSize: '18px' }}
												/>
												<FileLink fontSize={14}>
													{extractFilenameFromS3Key(insuranceFile.name)}
												</FileLink>
											</Box>
											<Box display="flex" alignItems="center" gap={8}>
												<EyeOutlined
													style={{
														cursor: 'pointer',
														color: '#1890ff',
														fontSize: '16px',
													}}
													onClick={() =>
														handleDocumentPreview(
															values.insurance_document_key,
															insuranceFile.name
														)
													}
												/>
												{!disabled && (
													<CloseOutlined
														style={{
															cursor: 'pointer',
															color: '#ff4d4f',
															fontSize: '14px',
														}}
														onClick={() =>
															handleFileRemove('insurance')
														}
													/>
												)}
											</Box>
										</FileItem>
									)}
									<FieldError
										error={form.values.vehicleErrors?.insurance_document_key}
										touched={form.values.insurance_document_key_touched}
									/>
								</>
							)}
						</Field>
					</FormField>
				</Col>
				<Col span={6}>
					<FormField label="Valid Till" required={!values.skip_document_upload}>
						<Field name="insurance_valid_till">
							{({ field, form }) => (
								<>
									<StyledDatePicker
										{...field}
										value={form.values.insurance_valid_till}
										placeholder="Eg : 12-2-2025"
										format="DD-MM-YYYY"
										disabled={disabled || values.skip_document_upload}
										onChange={(date) => {
											form.setFieldValue('insurance_valid_till', date);
											// Clear error when user selects a date
											if (form.values.vehicleErrors?.insurance_valid_till) {
												const newErrors = { ...form.values.vehicleErrors };
												delete newErrors.insurance_valid_till;
												form.setFieldValue('vehicleErrors', newErrors);
												form.setFieldValue(
													'insurance_valid_till_touched',
													false
												);
											}
										}}
										status={
											form.values.vehicleErrors?.insurance_valid_till &&
											form.values.insurance_valid_till_touched
												? 'error'
												: ''
										}
									/>
									<FieldError
										error={form.values.vehicleErrors?.insurance_valid_till}
										touched={form.values.insurance_valid_till_touched}
									/>
								</>
							)}
						</Field>
					</FormField>
				</Col>
			</Row>

			{/* PUC Doc Upload & Valid Till, Owner Ship Type */}
			<Row gutter={[24, 0]}>
				<Col span={6}>
					<FormField label="PUC Doc Upload">
						<Field name="puc_doc_file">
							{({ form }) => (
								<>
									<BrowseButton
										icon={<UploadOutlined />}
										loading={uploading.puc_doc}
										disabled={disabled || values.skip_document_upload}
										onClick={() =>
											!disabled &&
											!uploading.puc_doc &&
											handleFileUploadClick('puc_doc')
										}
										style={{ width: '100%' }}
									>
										{uploading.puc_doc ? UPLOADING_TEXT : BROWSE_FILE_TEXT}
									</BrowseButton>
									{pucFile && (
										<FileItem>
											<Box display="flex" alignItems="center" gap={8}>
												<FilePdfOutlined
													style={{ color: '#ff4d4f', fontSize: '18px' }}
												/>
												<FileLink fontSize={14}>
													{extractFilenameFromS3Key(pucFile.name)}
												</FileLink>
											</Box>
											<Box display="flex" alignItems="center" gap={8}>
												<EyeOutlined
													style={{
														cursor: 'pointer',
														color: '#1890ff',
														fontSize: '16px',
													}}
													onClick={() =>
														handleDocumentPreview(
															values.puc_document_key,
															pucFile.name
														)
													}
												/>
												{!disabled && (
													<CloseOutlined
														style={{
															cursor: 'pointer',
															color: '#ff4d4f',
															fontSize: '14px',
														}}
														onClick={() => handleFileRemove('puc_doc')}
													/>
												)}
											</Box>
										</FileItem>
									)}
									<FieldError
										error={form.values.vehicleErrors?.puc_document_key}
										touched={form.values.puc_document_key_touched}
									/>
								</>
							)}
						</Field>
					</FormField>
				</Col>
				<Col span={6}>
					<FormField label="Valid Till" required={!!values.puc_document_key}>
						<Field name="puc_valid_till">
							{({ field, form }) => (
								<>
									<StyledDatePicker
										{...field}
										value={form.values.puc_valid_till}
										placeholder="Eg : 12-2-2025"
										format="DD-MM-YYYY"
										disabled={disabled || values.skip_document_upload}
										onChange={(date) => {
											form.setFieldValue('puc_valid_till', date);
											// Clear error when user selects a date
											if (form.values.vehicleErrors?.puc_valid_till) {
												const newErrors = { ...form.values.vehicleErrors };
												delete newErrors.puc_valid_till;
												form.setFieldValue('vehicleErrors', newErrors);
												form.setFieldValue('puc_valid_till_touched', false);
											}
										}}
										status={
											form.values.vehicleErrors?.puc_valid_till &&
											form.values.puc_valid_till_touched
												? 'error'
												: ''
										}
									/>
									<FieldError
										error={form.values.vehicleErrors?.puc_valid_till}
										touched={form.values.puc_valid_till_touched}
									/>
								</>
							)}
						</Field>
					</FormField>
				</Col>
				<Col span={12}>
					<FormField label="Owner Ship Type" required={!values.skip_document_upload}>
						<Field name="ownership_type">
							{({ field, form }) => (
								<>
									<StyledSelect
										{...field}
										placeholder="Select"
										disabled={disabled || values.skip_document_upload}
										value={form.values.ownership_type}
										onChange={(value) => {
											form.setFieldValue('ownership_type', value);
											// Clear consent letter if changing to Own
											if (value === 'Own') {
												form.setFieldValue('consent_letter_file', null);
												form.setFieldValue('consent_letter_key', null);
											}
											// Clear error when user selects a value
											if (form.values.vehicleErrors?.ownership_type) {
												const newErrors = { ...form.values.vehicleErrors };
												delete newErrors.ownership_type;
												form.setFieldValue('vehicleErrors', newErrors);
												form.setFieldValue('ownership_type_touched', false);
											}
										}}
										status={
											form.values.vehicleErrors?.ownership_type &&
											form.values.ownership_type_touched
												? 'error'
												: ''
										}
									>
										<Option value="Own">Own</Option>
										<Option value="Aggregator">Aggregator</Option>
									</StyledSelect>
									<FieldError
										error={form.values.vehicleErrors?.ownership_type}
										touched={form.values.ownership_type_touched}
									/>
								</>
							)}
						</Field>
					</FormField>
				</Col>
			</Row>

			{/* Upload Consent Letter */}
			<Row gutter={[24, 0]}>
				<Col span={24}>
					<FormField
						label="Upload Consent Letter"
						required={
							!values.skip_document_upload && values.ownership_type === 'Aggregator'
						}
					>
						<Field name="consent_letter_file">
							{({ form }) => (
								<>
									<BrowseButton
										icon={<UploadOutlined />}
										loading={uploading.consent_letter}
										disabled={
											disabled ||
											!values.ownership_type ||
											values.skip_document_upload
										}
										onClick={() =>
											!disabled &&
											!uploading.consent_letter &&
											handleFileUploadClick('consent_letter')
										}
										style={{ width: '100%' }}
									>
										{uploading.consent_letter
											? UPLOADING_TEXT
											: BROWSE_FILE_TEXT}
									</BrowseButton>
									{consentLetterFile && (
										<FileItem>
											<Box display="flex" alignItems="center" gap={8}>
												<FilePdfOutlined
													style={{ color: '#ff4d4f', fontSize: '18px' }}
												/>
												<FileLink fontSize={14}>
													{extractFilenameFromS3Key(
														consentLetterFile.name
													)}
												</FileLink>
											</Box>
											<Box display="flex" alignItems="center" gap={8}>
												<EyeOutlined
													style={{
														cursor: 'pointer',
														color: '#1890ff',
														fontSize: '16px',
													}}
													onClick={() =>
														handleDocumentPreview(
															values.consent_letter_key,
															consentLetterFile.name
														)
													}
												/>
												{!disabled && (
													<CloseOutlined
														style={{
															cursor: 'pointer',
															color: '#ff4d4f',
															fontSize: '14px',
														}}
														onClick={() =>
															handleFileRemove('consent_letter')
														}
													/>
												)}
											</Box>
										</FileItem>
									)}
									<FieldError
										error={form.values.vehicleErrors?.consent_letter_key}
										touched={form.values.consent_letter_key_touched}
									/>
								</>
							)}
						</Field>
					</FormField>
				</Col>
			</Row>
			<Row gutter={[24, 0]}>
				<Col span={12}>
					<FormField label="GPS Installed" required={!values.skip_document_upload}>
						<Field name="gps_installed">
							{({ form }) => (
								<>
									<StyledSelect
										placeholder="Select"
										disabled={disabled || values.skip_document_upload}
										value={form.values.gps_installed}
										onChange={(value) => {
											form.setFieldValue('gps_installed', value);
											if (value !== true) {
												form.setFieldValue('gps_provider_name', '');
											}
											if (form.values.vehicleErrors?.gps_installed) {
												const newErrors = { ...form.values.vehicleErrors };
												delete newErrors.gps_installed;
												form.setFieldValue('vehicleErrors', newErrors);
												form.setFieldValue('gps_installed_touched', false);
											}
										}}
										status={
											form.values.vehicleErrors?.gps_installed &&
											form.values.gps_installed_touched
												? 'error'
												: ''
										}
									>
										<Option value>Yes</Option>
										<Option value={false}>No</Option>
									</StyledSelect>
									<FieldError
										error={form.values.vehicleErrors?.gps_installed}
										touched={form.values.gps_installed_touched}
									/>
								</>
							)}
						</Field>
					</FormField>
				</Col>
				{values.gps_installed === true && !values.skip_document_upload && (
					<Col span={12}>
						<FormField label="GPS Provider Name" required>
							<Field name="gps_provider_name">
								{({ field, form }) => (
									<>
										<StyledInput
											{...field}
											placeholder="Enter Provider Name"
											disabled={disabled}
											onChange={(e) => {
												form.setFieldValue(
													'gps_provider_name',
													e.target.value
												);
												if (form.values.vehicleErrors?.gps_provider_name) {
													const newErrors = {
														...form.values.vehicleErrors,
													};
													delete newErrors.gps_provider_name;
													form.setFieldValue('vehicleErrors', newErrors);
													form.setFieldValue(
														'gps_provider_name_touched',
														false
													);
												}
											}}
											status={
												form.values.vehicleErrors?.gps_provider_name &&
												form.values.gps_provider_name_touched
													? 'error'
													: ''
											}
										/>
										<FieldError
											error={form.values.vehicleErrors?.gps_provider_name}
											touched={form.values.gps_provider_name_touched}
										/>
									</>
								)}
							</Field>
						</FormField>
					</Col>
				)}
			</Row>

			{/* Skip Document Upload */}
			<Row gutter={[24, 0]}>
				<Col span={24}>
					<FormField label="Skip Document Upload">
						<SkipDocumentUploadRow style={{ opacity: disabled ? 0.8 : 1 }}>
							<SkipDocumentCheckbox
								checked={values.skip_document_upload}
								disabled={disabled}
								onChange={(e) => {
									setFieldValue('skip_document_upload', e.target.checked);
									if (e.target.checked) {
										setFieldValue('rc_doc_file', null);
										setFieldValue('rc_document_key', null);
										setFieldValue('rc_valid_till', null);
										setFieldValue('insurance_file', null);
										setFieldValue('insurance_document_key', null);
										setFieldValue('insurance_valid_till', null);
										setFieldValue('puc_doc_file', null);
										setFieldValue('puc_document_key', null);
										setFieldValue('puc_valid_till', null);
										setFieldValue('ownership_type', null);
										setFieldValue('consent_letter_file', null);
										setFieldValue('consent_letter_key', null);
										setFieldValue('gps_installed', null);
										setFieldValue('gps_provider_name', '');

										setRcDocFile(null);
										setInsuranceFile(null);
										setPucFile(null);
										setConsentLetterFile(null);
									}
								}}
							>
								SKIP DOCUMENT UPLOAD
							</SkipDocumentCheckbox>
							<Tooltip
								title="A vehicle can be onboarded by skipping the documents for now.But a trip cannot be closed if the vehicle documents are not present."
								placement="right"
							>
								<SkipDocumentInfoIcon>i</SkipDocumentInfoIcon>
							</Tooltip>
						</SkipDocumentUploadRow>
					</FormField>
				</Col>
			</Row>

			<Box display="flex" justifyContent="center" mt="16px">
				<AddVehicleButton
					disabled={disabled}
					onClick={() => !disabled && handleAddVehicle()}
				>
					Add Vehicle
				</AddVehicleButton>
			</Box>

			{/* Vehicle Details Table */}
			<Box mt="40px">
				<SectionTitle style={{ marginBottom: '16px' }}>Vehicle Details</SectionTitle>
				<StyledTable columns={columns} dataSource={vehicles} pagination={false} />
			</Box>

			{/* Vehicle Details Modal */}
			{selectedVehicle && editingVehicle && (
				<VehicleDetailsModal
					visible={modalVisible}
					editingVehicle={editingVehicle}
					setEditingVehicle={setEditingVehicle}
					selectedVehicle={selectedVehicle}
					disabled={disabled}
					savingVehicle={savingVehicle}
					vehicleTypeOptions={vehicleTypeOptions}
					loadingVehicleTypes={loadingVehicleTypes}
					onClose={handleModalClose}
					onSave={handleSaveChanges}
					onRemove={handleRemoveVehicle}
					handleDocumentPreview={handleDocumentPreview}
					FormField={FormField}
					StyledSelect={StyledSelect}
					StyledInput={StyledInput}
					StyledDatePicker={StyledDatePicker}
					BrowseButton={BrowseButton}
					RemoveButton={RemoveButton}
					SaveButton={SaveButton}
					FilePreview={FilePreview}
					FileIcon={FileIcon}
					FileName={FileName}
					FileSize={FileSize}
					RemoveFileButton={RemoveFileButton}
					ConsentLetterBrowseButton={ConsentLetterBrowseButton}
					DownloadOutlined={DownloadOutlined}
					EyeOutlined={EyeOutlined}
					Option={Option}
					message={message}
				/>
			)}

			{/* File Preview Modal */}
			<FilePreviewModal
				visible={filePreviewVisible}
				fileKey={previewFileKey}
				fileName={previewFileName}
				entity="vendor"
				onClose={handleFilePreviewClose}
			/>

			{/* Bulk Upload Modal */}
			<BulkUploadModal
				visible={bulkUploadModalVisible}
				onClose={handleBulkUploadModalClose}
				onSubmit={handleBulkUploadSubmit}
				onFileUpload={handleBulkUploadFileUpload}
				loading={bulkUploadLoading}
				title="Bulk Upload Vehicles"
				description="Please upload a CSV file to add multiple vehicles at once."
				templateUrl="/vehicle_bulk_upload_template_v2.csv"
				templateText="Download the CSV template:"
				acceptedFormats=".csv"
				maxSizeMB={10}
			/>
		</>
	);
}

VehicleInformationStep.propTypes = {
	isEditable: PropTypes.bool,
	isDisabled: PropTypes.bool,
};
